import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { usePageContext } from '../context/PageContext';
import { supabase } from '../lib/supabase';
import { useNavigate, useParams } from 'react-router-dom';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer
} from 'recharts';
import { ArrowLeft, Users, Clock, AlertTriangle, Trophy, ChevronLeft, ChevronRight, ChevronDown, ExternalLink } from 'lucide-react';
import { normalizeFormula, computeStarScore } from '../lib/starScore';
import { defaultPolicy } from '../lib/issuePolicy';
import {
  ISSUE_SELECT_COLUMNS, countableIssues, countOpenIssues, countAllIssues, partitionIssues,
} from '../lib/issueCounting';
import {
  resolveRubricDimensions, getDimensionRatings, ratingToScore, QUALITY_SELECT_COLUMNS,
} from '../lib/qualityDimensions';

const SESSIONS_PER_PAGE = 15;
const ISSUES_COLLAPSED_LIMIT = 8;

// Humanised labels for the risk categories the pipeline emits. Anything not
// listed falls back to title-cased snake_case so new categories still read well.
const ISSUE_CATEGORY_LABELS = {
  inappropriate_discussion: 'Inappropriate Discussion',
  poaching_attempt: 'Poaching Risk',
  poaching: 'Poaching Attempts',
  disparagement_of_organization: 'Disparagement',
  speaking_poorly: 'Speaking Poorly of Organization',
  factual_teaching_error: 'Teaching Error',
  incorrect_information: 'Incorrect Information',
  poor_conduct: 'Poor Conduct / Professionalism',
  session_cut_short: 'Session Cut Short',
  giving_answers: 'Giving Answers (Not Teaching)',
  lack_of_preparation: 'Lack of Preparation',
  dismissive_of_questions: 'Dismissive of Questions',
  confusing_explanations: 'Confusing Explanations',
  tutor_lateness: 'Tutor Lateness',
};

const humaniseCategory = (category) => {
  if (!category) return 'Unknown';
  if (ISSUE_CATEGORY_LABELS[category]) return ISSUE_CATEGORY_LABELS[category];
  return String(category).replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
};

// Buckets and their ordering come from lib/issueCounting.js (`bucketForStatus`),
// so `new` — and anything unrecognised — is treated as open/unresolved.
const ISSUE_BUCKETS = [
  { key: 'open', label: 'Open' },
  { key: 'under_review', label: 'Under review' },
  { key: 'closed', label: 'Resolved & dismissed' },
];

const STATUS_LABELS = {
  new: 'Open',
  under_review: 'Under review',
  resolved: 'Resolved',
  dismissed: 'Dismissed',
};

// Every `.in('session_id', ...)` read is chunked (long URLs fail) and paged
// (PostgREST caps a response at 1000 rows), so counts never silently truncate.
const ID_CHUNK_SIZE = 200;
const ROW_PAGE_SIZE = 1000;
const fetchBySessionIds = async (table, select, ids) => {
  if (!Array.isArray(ids) || ids.length === 0) return [];
  const rows = [];
  for (let i = 0; i < ids.length; i += ID_CHUNK_SIZE) {
    const idChunk = ids.slice(i, i + ID_CHUNK_SIZE);
    for (let from = 0; ; from += ROW_PAGE_SIZE) {
      const { data, error } = await supabase
        .from(table)
        .select(select)
        .in('session_id', idChunk)
        .range(from, from + ROW_PAGE_SIZE - 1);
      if (error || !data) break;
      rows.push(...data);
      if (data.length < ROW_PAGE_SIZE) break;
    }
  }
  return rows;
};

// Extra display columns the issues list needs on top of the shared count columns.
// NB: risks_v2 has no `summary` column — selecting it 400s the whole fetch
// and every issue count on this page silently reads 0.
const ISSUE_DETAIL_COLUMNS =
  `${ISSUE_SELECT_COLUMNS}, why_flagged, timestamp_display, resolution_outcome, resolution_notes, resolved_at, created_at`;

const ratingToPct = (r) => {
  const score = ratingToScore(r);
  return score > 0 ? Math.round((score / 3) * 100) : null;
};

// Non-rubric columns; the org's quality dimensions are appended after these.
const BASE_SESSION_COLUMNS = [
  { key: 'quality', label: 'Quality' },
  { key: 'issues', label: 'Issues' },
  { key: 'punctuality', label: 'Punctuality' },
];
// Recharts' radar degenerates below three axes, so small rubrics fall back to
// the bar list instead of rendering a broken chart.
const MIN_RADAR_AXES = 3;

// Org-defined dimension labels can be long; keep radar axis text from colliding.
const shortLabel = (s, max = 16) =>
  typeof s === 'string' && s.length > max ? `${s.slice(0, max - 1)}…` : s;

const TutorDetail = () => {
  const { organizationId } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { tutorName: rawTutorName } = useParams();
  const tutorName = decodeURIComponent(rawTutorName);
  const { setSubtitle } = usePageContext();
  const isDark = theme === 'dark';
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionPage, setSessionPage] = useState(0);
  const [sessionColumns, setSessionColumns] = useState(['quality', 'issues']);
  // The org's teaching-quality rubric; defaults to the five built-in dimensions.
  const [rubricDims, setRubricDims] = useState(() => resolveRubricDimensions(null));
  const [expandedIssueBuckets, setExpandedIssueBuckets] = useState({ open: true, under_review: true, closed: false });
  const [showAllIssueBuckets, setShowAllIssueBuckets] = useState({});

  useEffect(() => {
    if (!organizationId || !tutorName) return;
    setSubtitle(tutorName);
    loadDetail();
    return () => setSubtitle('');
  }, [organizationId, tutorName]);

  const loadDetail = async () => {
    setLoading(true);
    try {
      const [{ data: sessions }, { data: allOrgSessions }, { data: orgRow }] = await Promise.all([
        supabase
          .from('sessions')
          .select('id, session_id, student_name, lesson_timestamp, duration_minutes, subject_covered, tutor_join_lateness_seconds, lateness_seconds')
          .eq('organization_id', organizationId)
          .eq('tutor_name', tutorName)
          .eq('is_processed_v2', true)
          .order('lesson_timestamp', { ascending: false }),
        supabase
          .from('sessions')
          .select('id, tutor_name, duration_minutes, tutor_join_lateness_seconds, lateness_seconds')
          .eq('organization_id', organizationId)
          .eq('is_processed_v2', true),
        supabase
          .from('organizations')
          .select('star_score_formula, needs_attention_thresholds, issue_filter_policy, quality_rubric, lateness_minor_minutes')
          .eq('id', organizationId)
          .single(),
      ]);
      const starFormula = normalizeFormula(orgRow?.star_score_formula);
      const issuePolicy = orgRow?.issue_filter_policy || defaultPolicy();
      // A lesson counts as late from the org's minor-lateness threshold (default
      // 3 min), the same rule as the Punctuality page; under it, it's on time.
      const lateThresholdSec = (orgRow?.lateness_minor_minutes ?? 3) * 60;
      const dims = resolveRubricDimensions(orgRow?.quality_rubric);
      setRubricDims(dims);

      const sessData = sessions || [];
      const sessionIds = sessData.map(s => s.id);

      const [qaRows, riskRows] = await Promise.all([
        fetchBySessionIds('quality_assessments', `session_id, insufficient_evidence, ${QUALITY_SELECT_COLUMNS}`, sessionIds),
        fetchBySessionIds('risks_v2', ISSUE_DETAIL_COLUMNS, sessionIds),
      ]);

      const qaBySession = {};
      // Skip abstaining assessments (insufficient transcript evidence) — they must not drag averages down
      qaRows.forEach(qa => { if (!qa.insufficient_evidence) qaBySession[qa.session_id] = qa; });
      // Single source of truth for "flagged issue": lib/issueCounting.js drops
      // tutor_lateness (it has its own Punctuality surfaces) and guardrail-
      // suppressed risks, the latter read off the LLM-stamped filter_status —
      // never keyword matching.
      const visibleRisks = countableIssues(riskRows, issuePolicy);

      const risksBySession = {};
      visibleRisks.forEach(r => {
        if (!risksBySession[r.session_id]) risksBySession[r.session_id] = [];
        risksBySession[r.session_id].push(r);
      });

      // key -> summed rating score across this tutor's assessments
      const dimSums = {};
      dims.forEach(d => { dimSums[d.key] = 0; });
      let qaCount = 0;
      let onTimeCount = 0;
      let totalIssues = 0;

      sessData.forEach(s => {
        const qa = qaBySession[s.id];
        if (qa) {
          const ratings = getDimensionRatings(qa);
          dims.forEach(d => { dimSums[d.key] += ratingToScore(ratings[d.key]); });
          qaCount++;
        }
        const late = s.tutor_join_lateness_seconds || s.lateness_seconds || 0;
        if (late < lateThresholdSec) onTimeCount++;
        totalIssues += countOpenIssues(risksBySession[s.id], issuePolicy);
      });

      const pct = (val, count) => count > 0 ? Math.round((val / count / 3) * 100) : 0;
      const punctualityPct = sessData.length > 0 ? Math.round((onTimeCount / sessData.length) * 100) : 100;

      // Punctuality is not a rubric dimension but is plotted alongside them.
      const rubricRadar = dims.map(d => ({ dimension: d.label, value: pct(dimSums[d.key], qaCount) }));
      const radarData = [
        { dimension: 'Punctuality', value: punctualityPct },
        ...rubricRadar,
      ];

      const overallPct = radarData.reduce((s, d) => s + d.value, 0) / radarData.length;
      const tier = overallPct >= 75 ? 'Excellent' : overallPct >= 50 ? 'On track' : overallPct >= 25 ? 'Needs attention' : 'At risk';
      const tierColor = overallPct >= 75 ? '#10b981' : overallPct >= 50 ? '#eab308' : overallPct >= 25 ? '#f97316' : '#ef4444';

      // Strengths/focus come from the rubric dimensions only (never punctuality),
      // keyed off the rubric list so a dimension literally named "Punctuality"
      // is still counted. Two each once there are enough dimensions to avoid
      // listing the same one on both sides.
      const dimScores = [...rubricRadar].sort((a, b) => b.value - a.value);
      const takeCount = dimScores.length >= 4 ? 2 : 1;
      const strengths = dimScores.slice(0, takeCount);
      const focusAreas = dimScores.length > takeCount ? dimScores.slice(-takeCount).reverse() : [];

      // ---- Rank using the configured Star Score formula ----
      const orgSessions = allOrgSessions || [];
      const allSessionIds = orgSessions.map(s => s.id);

      const [allQa, allRisks] = await Promise.all([
        fetchBySessionIds('quality_assessments', `session_id, insufficient_evidence, ${QUALITY_SELECT_COLUMNS}`, allSessionIds),
        // ISSUE_SELECT_COLUMNS carries category + filter_status, which the shared
        // issue rules need — the rank must be built on the same issue definition
        // the Tutors list uses, or the "#N of M" disagrees between pages.
        fetchBySessionIds('risks_v2', ISSUE_SELECT_COLUMNS, allSessionIds),
      ]);
      const qaById = {};
      allQa.forEach(qa => { if (!qa.insufficient_evidence) qaById[qa.session_id] = qa; });
      const risksByOrgSession = {};
      allRisks.forEach(r => {
        if (!r) return;
        if (!risksByOrgSession[r.session_id]) risksByOrgSession[r.session_id] = [];
        risksByOrgSession[r.session_id].push(r);
      });
      const openRiskCountById = {};
      Object.entries(risksByOrgSession).forEach(([sessionId, risks]) => {
        openRiskCountById[sessionId] = countOpenIssues(risks, issuePolicy);
      });

      const aggByTutor = {};
      orgSessions.forEach(s => {
        if (!s.tutor_name) return;
        let a = aggByTutor[s.tutor_name];
        if (!a) {
          const sums = {};
          dims.forEach(d => { sums[d.key] = 0; });
          a = { sessions: 0, qaCount: 0, sums, mins: 0, onTime: 0, openIssues: 0 };
        }
        a.sessions++;
        a.mins += s.duration_minutes || 0;
        const lateSec = s.tutor_join_lateness_seconds || s.lateness_seconds || 0;
        if (lateSec < lateThresholdSec) a.onTime++;
        a.openIssues += openRiskCountById[s.id] || 0;
        const qa = qaById[s.id];
        if (qa) {
          const ratings = getDimensionRatings(qa);
          dims.forEach(d => { a.sums[d.key] += ratingToScore(ratings[d.key]); });
          a.qaCount++;
        }
        aggByTutor[s.tutor_name] = a;
      });

      const maxHours = Math.max(1, ...Object.values(aggByTutor).map(a => a.mins / 60));
      const pctVal = (v, n) => n > 0 ? (v / n / 3) * 100 : 0;
      const tutorScores = Object.entries(aggByTutor).map(([name, a]) => {
        const dimPcts = {};
        dims.forEach(d => { dimPcts[d.key] = pctVal(a.sums[d.key] || 0, a.qaCount); });
        const composite = computeStarScore({
          dimensions: dimPcts,
          qaCount: a.qaCount,
          volumeShare: (a.mins / 60) / maxHours,
          issueRate: a.sessions > 0 ? a.openIssues / a.sessions : 0,
          onTimePct: a.sessions > 0 ? (a.onTime / a.sessions) * 100 : 100,
        }, starFormula);
        return { name, composite, sessions: a.sessions };
      });

      tutorScores.sort((x, y) => (y.composite - x.composite) || (y.sessions - x.sessions));
      const totalTutors = tutorScores.length;
      const rankIdx = tutorScores.findIndex(t => t.name === tutorName);
      const rank = rankIdx >= 0 ? rankIdx + 1 : null;

      // ---- Flagged issues, grouped by resolution status ----
      const sessionById = {};
      sessData.forEach(s => { sessionById[s.id] = s; });

      const toIssueRow = (r) => {
        const sess = sessionById[r.session_id] || null;
        const dateVal = sess?.lesson_timestamp || r.created_at || null;
        const parsedDate = dateVal ? new Date(dateVal) : null;
        const dateMs = parsedDate && !isNaN(parsedDate.getTime()) ? parsedDate.getTime() : 0;
        const status = r.status || 'new';
        return {
          id: r.id,
          sessionId: r.session_id,
          student: sess?.student_name || null,
          subject: sess?.subject_covered || null,
          date: dateMs > 0 ? dateVal : null,
          dateMs,
          category: humaniseCategory(r.category),
          severity: typeof r.severity === 'number' ? r.severity : null,
          summary: (r.summary || r.why_flagged || '').trim(),
          status,
          statusLabel: STATUS_LABELS[status] || humaniseCategory(status),
          timestampDisplay: r.timestamp_display || null,
          resolutionOutcome: r.resolution_outcome || null,
          resolutionNotes: r.resolution_notes || null,
        };
      };

      // Bucketing comes from the shared partition, so the list, the bucket
      // counts and the Issues KPI can never drift apart.
      const partitioned = partitionIssues(visibleRisks, issuePolicy);
      const issueGroups = { open: [], under_review: [], closed: [] };
      Object.keys(issueGroups).forEach(key => {
        // Severity descending, then most recent lesson first.
        issueGroups[key] = (partitioned[key] || []).map(toIssueRow)
          .sort((a, b) => ((b.severity || 0) - (a.severity || 0)) || (b.dateMs - a.dateMs));
      });

      setDetail({
        name: tutorName,
        radarData,
        overallPct: Math.round(overallPct),
        tier,
        tierColor,
        strengths,
        focusAreas,
        totalSessions: sessData.length,
        qualityPct: qaCount > 0 && dims.length > 0
          ? pct(dims.reduce((sum, d) => sum + dimSums[d.key], 0), qaCount * dims.length)
          : 0,
        rank,
        totalTutors,
        students: new Set(sessData.map(s => s.student_name).filter(Boolean)).size,
        onTimePct: punctualityPct,
        totalIssues,
        totalHours: Math.round(sessData.reduce((s, sess) => s + (sess.duration_minutes || 0), 0) / 60 * 10) / 10,
        recentSessions: sessData.map(s => {
          const qa = qaBySession[s.id];
          const ratings = qa ? getDimensionRatings(qa) : {};
          const scores = qa ? dims.map(d => ratingToScore(ratings[d.key])).filter(x => x > 0) : [];
          const sessQuality = scores.length > 0 ? Math.round((scores.reduce((a, b) => a + b, 0) / scores.length / 3) * 100) : null;
          const openRiskCount = countOpenIssues(risksBySession[s.id], issuePolicy);
          const lateSec = s.tutor_join_lateness_seconds || s.lateness_seconds || 0;
          const onTime = lateSec < lateThresholdSec;
          return {
            id: s.id,
            student: s.student_name,
            subject: s.subject_covered,
            date: s.lesson_timestamp,
            duration: s.duration_minutes,
            quality: sessQuality,
            risks: openRiskCount,
            punctuality: onTime ? 100 : 0,
            punctualityLabel: onTime ? 'On time' : `${Math.round(lateSec / 60)}m late`,
            // rubric key -> 0-100 (or null when unrated), read via the shared helper
            dimPcts: Object.fromEntries(dims.map(d => [d.key, ratingToPct(ratings[d.key])])),
          };
        }),
        issueGroups,
        totalFlaggedIssues: countAllIssues(visibleRisks, issuePolicy),
      });
    } catch (err) {
      console.error('Error loading tutor detail:', err);
    }
    setLoading(false);
  };

  const cardClass = `rounded-lg border ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`;
  // Base columns first, then one toggle per rubric dimension.
  // Rubric columns are namespaced so an org-defined key can never collide with
  // a base column key (e.g. a dimension literally keyed "quality").
  const sessionHistoryColumns = useMemo(
    () => [
      ...BASE_SESSION_COLUMNS.map(c => ({ ...c, id: c.key, isDim: false })),
      ...rubricDims.map(d => ({ id: `dim:${d.key}`, key: d.key, label: d.label, isDim: true })),
    ],
    [rubricDims],
  );
  // A 1- or 2-dimension rubric can't fill a radar, so show the bar list instead.
  const canRenderRadar = rubricDims.length >= MIN_RADAR_AXES;
  const visibleSessionColumns = sessionHistoryColumns.filter(c => sessionColumns.includes(c.id));
  const sessionGridTemplate = `minmax(150px, 1.5fr) minmax(120px, 1fr) minmax(160px, 1.3fr) minmax(80px, 0.7fr) ${visibleSessionColumns.map(() => 'minmax(90px, 0.8fr)').join(' ')}`;
  // Rows need this much width before columns start squashing; the table scrolls
  // sideways inside its card beyond that instead of stretching the whole page.
  const sessionGridMinWidth = 150 + 120 + 160 + 80 + visibleSessionColumns.length * 90 + (3 + visibleSessionColumns.length) * 12 + 40;

  const toggleIssueBucket = (key) => {
    setExpandedIssueBuckets(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleShowAllIssues = (key) => {
    setShowAllIssueBuckets(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const openIssue = (issue) => {
    if (!issue?.sessionId) return;
    const path = issue.timestampDisplay
      ? `/lessons/${issue.sessionId}?tab=transcript&t=${encodeURIComponent(issue.timestampDisplay)}`
      : `/lessons/${issue.sessionId}`;
    navigate(path);
  };

  const toggleSessionColumn = (id) => {
    setSessionColumns(prev => prev.includes(id) ? prev.filter(k => k !== id) : [...prev, id]);
  };

  const renderSessionColumn = (column, session) => {
    if (column.isDim) {
      const value = session.dimPcts ? session.dimPcts[column.key] : null;
      return value != null ? `${value}%` : '--';
    }
    if (column.key === 'quality') {
      return session.quality != null ? `${session.quality}%` : '--';
    }
    if (column.key === 'issues') {
      return session.risks;
    }
    if (column.key === 'punctuality') {
      return session.punctualityLabel;
    }
    return '--';
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className={`h-6 w-32 rounded ${isDark ? 'bg-[#27272a]' : 'bg-[#e5e5e5]'}`} />
        <div className={`h-8 w-48 rounded ${isDark ? 'bg-[#27272a]' : 'bg-[#e5e5e5]'}`} />
        <div className="grid grid-cols-5 gap-3">
          {[1,2,3,4,5].map(i => <div key={i} className={`${cardClass} h-20`} />)}
        </div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="space-y-6">
        <button onClick={() => navigate('/tutors')} className={`flex items-center gap-2 text-sm ${isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666] hover:text-[#1a1a1a]'}`}>
          <ArrowLeft className="h-4 w-4" /> Back to tutors
        </button>
        <p className={`text-sm ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Tutor not found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <button
        onClick={() => navigate('/tutors')}
        className={`flex items-center gap-2 text-sm ${isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666] hover:text-[#1a1a1a]'}`}
      >
        <ArrowLeft className="h-4 w-4" /> Back to tutors
      </button>

      <h1 className={`text-2xl font-semibold font-[Manrope] tracking-tight ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
        {detail.name}
      </h1>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Sessions', value: detail.totalSessions, icon: Users },
          {
            label: 'Rank',
            value: detail.rank ? `#${detail.rank}` : '—',
            suffix: detail.rank && detail.totalTutors ? `of ${detail.totalTutors}` : null,
            icon: Trophy,
          },
          { label: 'Students', value: detail.students, icon: Users },
          { label: 'On-time', value: `${detail.onTimePct}%`, icon: Clock },
          {
            label: 'Issues',
            // Open count, but the total is surfaced too: a tutor whose issues
            // are all actioned must not read "0" next to a non-empty list.
            value: detail.totalIssues,
            suffix: detail.totalFlaggedIssues > detail.totalIssues ? `of ${detail.totalFlaggedIssues}` : null,
            icon: AlertTriangle,
            warn: detail.totalIssues > 0,
            // Clickable whenever the list below has rows, not only when open > 0.
            scrollTo: detail.totalFlaggedIssues > 0 ? 'issues-section' : null,
          },
        ].map(kpi => (
          <div
            key={kpi.label}
            className={`${cardClass} p-4 ${kpi.scrollTo ? 'cursor-pointer hover:border-amber-400/50 transition-colors' : ''}`}
            onClick={() => {
              if (kpi.scrollTo) {
                document.getElementById(kpi.scrollTo)?.scrollIntoView({ behavior: 'smooth' });
              }
            }}
          >
            <div className="flex items-center justify-between mb-1">
              <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{kpi.label}</span>
              <kpi.icon className={`h-3.5 w-3.5 ${kpi.warn ? 'text-amber-400' : isDark ? 'text-[#52525b]' : 'text-[#999]'}`} strokeWidth={1.5} />
            </div>
            <div className={`text-xl font-semibold font-[Manrope] ${kpi.warn ? 'text-amber-400' : isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
              {kpi.value}
              {kpi.suffix && (
                <span className={`ml-1 text-xs font-normal ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{kpi.suffix}</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Radar Chart + Quality Snapshot */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className={`${cardClass} p-5`}>
          <h3 className={`text-sm font-medium mb-2 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
            {canRenderRadar ? 'Performance Radar' : 'Performance'}
          </h3>
          <div className="h-64">
            {canRenderRadar ? (
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={detail.radarData} cx="50%" cy="50%" outerRadius="75%">
                  <PolarGrid stroke={isDark ? '#27272a' : '#e5e5e5'} />
                  <PolarAngleAxis dataKey="dimension" tickFormatter={shortLabel} tick={{ fontSize: 11, fill: isDark ? '#a1a1aa' : '#666' }} />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} axisLine={false} />
                  <Radar isAnimationActive={false} dataKey="value" stroke={detail.tierColor} fill={detail.tierColor} fillOpacity={0.2} strokeWidth={2} />
                </RadarChart>
              </ResponsiveContainer>
            ) : (
              // Too few rubric dimensions for a readable radar — plain bars instead.
              <div className="h-full flex flex-col justify-center gap-3">
                {detail.radarData.map(d => (
                  <div key={d.dimension} className="flex items-center gap-2">
                    <span className={`text-sm flex-1 min-w-0 truncate ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`} title={d.dimension}>
                      {d.dimension}
                    </span>
                    <div className={`w-28 h-1.5 rounded-full overflow-hidden flex-shrink-0 ${isDark ? 'bg-[#27272a]' : 'bg-[#e5e5e5]'}`}>
                      <div className="h-full rounded-full" style={{ width: `${d.value}%`, backgroundColor: detail.tierColor }} />
                    </div>
                    <span className={`text-xs w-9 text-right flex-shrink-0 ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{d.value}%</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center justify-center gap-2 mt-2">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: detail.tierColor }} />
            <span className="text-sm font-medium" style={{ color: detail.tierColor }}>{detail.tier}</span>
            <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>({detail.overallPct}%)</span>
          </div>
        </div>

        <div className={`${cardClass} p-5`}>
          <h3 className={`text-sm font-medium mb-4 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Quality Snapshot</h3>

          {(detail.strengths || []).length > 0 && (
            <div className="mb-4">
              <div className={`text-xs font-medium uppercase tracking-wider mb-2 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>Strengths</div>
              {detail.strengths.map(s => (
                <div key={s.dimension} className="flex items-center justify-between gap-2 mb-2">
                  <span className={`text-sm min-w-0 truncate ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`} title={s.dimension}>{s.dimension}</span>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className={`w-24 h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-[#27272a]' : 'bg-[#e5e5e5]'}`}>
                      <div className="h-full rounded-full bg-emerald-500" style={{ width: `${s.value}%` }} />
                    </div>
                    <span className={`text-xs font-medium w-8 text-right ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{s.value}%</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {(detail.focusAreas || []).length > 0 && (
            <div className="mb-4">
              <div className={`text-xs font-medium uppercase tracking-wider mb-2 ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>Focus Areas</div>
              {detail.focusAreas.map(s => (
                <div key={s.dimension} className="flex items-center justify-between gap-2 mb-2">
                  <span className={`text-sm min-w-0 truncate ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`} title={s.dimension}>{s.dimension}</span>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className={`w-24 h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-[#27272a]' : 'bg-[#e5e5e5]'}`}>
                      <div className="h-full rounded-full bg-amber-500" style={{ width: `${s.value}%` }} />
                    </div>
                    <span className={`text-xs font-medium w-8 text-right ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{s.value}%</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className={`pt-4 border-t ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
            <div className={`text-xs font-medium uppercase tracking-wider mb-2 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>All Dimensions</div>
            {detail.radarData.map(d => (
              <div key={d.dimension} className="flex items-center justify-between gap-2 mb-1.5">
                <span className={`text-xs min-w-0 truncate ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`} title={d.dimension}>{d.dimension}</span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className={`w-20 h-1 rounded-full overflow-hidden ${isDark ? 'bg-[#27272a]' : 'bg-[#e5e5e5]'}`}>
                    <div className="h-full rounded-full accent-gradient" style={{ width: `${d.value}%` }} />
                  </div>
                  <span className={`text-xs w-8 text-right ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{d.value}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Session History */}
      {(() => {
        const allSessions = detail.recentSessions || [];
        const totalPages = Math.max(1, Math.ceil(allSessions.length / SESSIONS_PER_PAGE));
        const safePage = Math.min(sessionPage, totalPages - 1);
        const pageRows = allSessions.slice(safePage * SESSIONS_PER_PAGE, (safePage + 1) * SESSIONS_PER_PAGE);
        return (
      <div className={`${cardClass} min-w-0 max-w-full`}>
        <div className="p-5 pb-3 flex items-center justify-between">
          <h3 className={`text-sm font-medium ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Session History</h3>
          <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{allSessions.length} total</span>
        </div>
        <div className="px-5 pb-3 flex items-center gap-2 flex-wrap">
          <span className={`text-xs font-medium uppercase tracking-wider mr-1 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Columns</span>
          {sessionHistoryColumns.map(col => {
            const on = sessionColumns.includes(col.id);
            return (
              <button
                key={col.id}
                type="button"
                title={col.label}
                onClick={() => toggleSessionColumn(col.id)}
                className={`px-2.5 py-1 text-xs rounded-md border transition-colors max-w-[180px] truncate ${
                  on
                    ? isDark ? 'border-[#3f3f46] bg-[#27272a] text-white' : 'border-[#cccccc] bg-[#e8e8e8] text-[#1a1a1a]'
                    : isDark ? 'border-[#27272a] text-[#a1a1aa] hover:text-white' : 'border-[#e5e5e5] text-[#666] hover:text-[#1a1a1a]'
                }`}
              >
                {col.label}
              </button>
            );
          })}
        </div>
        <div className={`divide-y overflow-x-auto w-full max-w-full ${isDark ? 'divide-[#27272a]' : 'divide-[#e5e5e5]'}`}>
          <div
            className={`grid gap-3 px-5 py-2 text-xs font-medium uppercase tracking-wider ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}
            style={{ gridTemplateColumns: sessionGridTemplate, minWidth: sessionGridMinWidth }}
          >
            <span>Date</span>
            <span>Student</span>
            <span>Subject</span>
            <span>Duration</span>
            {visibleSessionColumns.map(col => <span key={col.id} className="truncate" title={col.label}>{col.label}</span>)}
          </div>
          {pageRows.map(s => (
            <div
              key={s.id}
              onClick={() => navigate(`/lessons/${s.id}`)}
              className={`grid gap-3 px-5 py-3 items-center cursor-pointer transition-colors ${isDark ? 'hover:bg-[#18181b]/50' : 'hover:bg-[#fafafa]'}`}
              style={{ gridTemplateColumns: sessionGridTemplate, minWidth: sessionGridMinWidth }}
            >
              <span className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                {s.date ? new Date(s.date).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }) : '--'}
              </span>
              <span className={`text-sm truncate ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{s.student || '--'}</span>
              <span className={`text-sm truncate ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{s.subject || '--'}</span>
              <span className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{s.duration ? `${s.duration}m` : '--'}</span>
              {visibleSessionColumns.map(col => {
                const value = renderSessionColumn(col, s);
                const numeric = col.key !== 'issues' && typeof value === 'string' && value.endsWith('%') ? Number(value.replace('%', '')) : null;
                const cellColor = col.key === 'issues' && s.risks > 0
                  ? 'text-amber-400'
                  : numeric == null
                    ? isDark ? 'text-[#a1a1aa]' : 'text-[#666]'
                    : numeric >= 75 ? isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'
                    : numeric >= 50 ? isDark ? 'text-[#a1a1aa]' : 'text-[#666]'
                    : 'text-amber-400';
                return (
                  <span key={col.id} className={`text-sm truncate ${cellColor}`}>
                    {col.key === 'issues' && s.risks > 0 && <AlertTriangle className="h-3 w-3 inline mr-1" />}
                    {value}
                  </span>
                );
              })}
            </div>
          ))}
        </div>
        {totalPages > 1 && (
          <div className={`flex items-center justify-center gap-2 py-3 border-t ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
            <button
              onClick={() => setSessionPage(Math.max(0, safePage - 1))}
              disabled={safePage === 0}
              className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-[#18181b] disabled:opacity-30' : 'hover:bg-[#f0f0f0] disabled:opacity-30'}`}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className={`text-xs ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
              Page {safePage + 1} of {totalPages}
            </span>
            <button
              onClick={() => setSessionPage(Math.min(totalPages - 1, safePage + 1))}
              disabled={safePage >= totalPages - 1}
              className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-[#18181b] disabled:opacity-30' : 'hover:bg-[#f0f0f0] disabled:opacity-30'}`}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
        );
      })()}

      {/* Flagged Issues — grouped by resolution status */}
      {(() => {
        const groups = detail.issueGroups || { open: [], under_review: [], closed: [] };
        const total = detail.totalFlaggedIssues || 0;
        const countClass = {
          open: 'text-amber-400',
          under_review: isDark ? 'text-sky-400' : 'text-sky-600',
          closed: isDark ? 'text-emerald-400' : 'text-emerald-600',
        };
        return (
          <div id="issues-section" className={cardClass}>
            <div className="p-5 pb-3 flex items-center justify-between gap-3">
              <h3 className={`text-sm font-medium ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Flagged Issues</h3>
              <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                {total} total
                {total > 0 && (
                  <>
                    <span className="mx-1.5">·</span>
                    <span className={countClass.open}>{(groups.open || []).length} open</span>
                  </>
                )}
              </span>
            </div>

            {total === 0 ? (
              <p className={`px-5 pb-5 text-sm ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                No flagged issues for this tutor.
              </p>
            ) : (
              <div className={`border-t ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
                {(groups.open || []).length === 0 && (
                  <p className={`px-5 py-3 text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                    No open issues — everything flagged for this tutor has been actioned.
                  </p>
                )}
                {ISSUE_BUCKETS.map(bucket => {
                  const rows = groups[bucket.key] || [];
                  if (rows.length === 0) return null;
                  const expanded = !!expandedIssueBuckets[bucket.key];
                  const showAll = !!showAllIssueBuckets[bucket.key];
                  const shown = showAll ? rows : rows.slice(0, ISSUES_COLLAPSED_LIMIT);
                  const hidden = rows.length - shown.length;
                  return (
                    <div key={bucket.key} className={`border-t first:border-t-0 ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
                      <button
                        type="button"
                        onClick={() => toggleIssueBucket(bucket.key)}
                        className={`w-full flex items-center gap-2 px-5 py-2.5 text-left transition-colors ${isDark ? 'hover:bg-[#18181b]/50' : 'hover:bg-[#fafafa]'}`}
                      >
                        <ChevronDown
                          className={`h-3.5 w-3.5 transition-transform ${expanded ? '' : '-rotate-90'} ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}
                        />
                        <span className={`text-xs font-medium uppercase tracking-wider ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                          {bucket.label}
                        </span>
                        <span className={`text-xs font-medium ${countClass[bucket.key]}`}>{rows.length}</span>
                      </button>

                      {expanded && (
                        <div className={`divide-y ${isDark ? 'divide-[#27272a]' : 'divide-[#e5e5e5]'}`}>
                          {shown.map(issue => (
                            <div
                              key={issue.id}
                              onClick={() => openIssue(issue)}
                              className={`px-5 py-3.5 transition-colors ${issue.sessionId ? 'cursor-pointer' : ''} ${isDark ? 'hover:bg-[#18181b]/50' : 'hover:bg-[#fafafa]'}`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                                      issue.severity >= 8 ? 'bg-red-500/10 text-red-400'
                                      : issue.severity >= 5 ? 'bg-amber-500/10 text-amber-400'
                                      : isDark ? 'bg-[#27272a] text-[#a1a1aa]' : 'bg-[#f0f0f0] text-[#666]'
                                    }`}>
                                      <AlertTriangle className="h-3 w-3" />
                                      {issue.category}
                                    </span>
                                    <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                                      {issue.severity != null ? `Severity ${issue.severity}/10` : 'Severity --'}
                                    </span>
                                    {bucket.key !== 'open' && (
                                      <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-[#27272a] text-[#a1a1aa]' : 'bg-[#f0f0f0] text-[#666]'}`}>
                                        {issue.statusLabel}
                                      </span>
                                    )}
                                    {issue.timestampDisplay && (
                                      <span className={`inline-flex items-center gap-1 text-xs ${isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}`}>
                                        <ExternalLink className="h-3 w-3" />
                                        {issue.timestampDisplay}
                                      </span>
                                    )}
                                  </div>
                                  {issue.summary ? (
                                    <p className={`text-sm leading-relaxed ${isDark ? 'text-[#a1a1aa]' : 'text-[#555]'}`}>
                                      {issue.summary.length > 220 ? `${issue.summary.slice(0, 220)}…` : issue.summary}
                                    </p>
                                  ) : (
                                    <p className={`text-sm italic ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>No summary recorded.</p>
                                  )}
                                  <div className={`flex items-center gap-3 mt-1.5 text-xs flex-wrap ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                                    <span>{issue.student || 'Unknown student'}</span>
                                    <span>·</span>
                                    <span>{issue.date ? new Date(issue.date).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }) : '--'}</span>
                                    {issue.subject && <><span>·</span><span>{issue.subject}</span></>}
                                  </div>
                                  {(issue.resolutionOutcome || issue.resolutionNotes) && (
                                    <div className={`mt-1.5 text-xs ${isDark ? 'text-[#71717a]' : 'text-[#777]'}`}>
                                      {issue.resolutionOutcome && <span className="font-medium">{humaniseCategory(issue.resolutionOutcome)}</span>}
                                      {issue.resolutionOutcome && issue.resolutionNotes && <span> — </span>}
                                      {issue.resolutionNotes && (
                                        <span>{issue.resolutionNotes.length > 160 ? `${issue.resolutionNotes.slice(0, 160)}…` : issue.resolutionNotes}</span>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <ChevronRight className={`h-4 w-4 flex-shrink-0 mt-1 ${isDark ? 'text-[#52525b]' : 'text-[#ccc]'}`} />
                              </div>
                            </div>
                          ))}
                          {(hidden > 0 || showAll) && (
                            <button
                              type="button"
                              onClick={() => toggleShowAllIssues(bucket.key)}
                              className={`w-full px-5 py-2.5 text-xs text-left transition-colors ${isDark ? 'text-[#7662ac] hover:bg-[#18181b]/50' : 'text-[#586f8d] hover:bg-[#fafafa]'}`}
                            >
                              {showAll ? 'Show fewer' : `Show all ${rows.length}`}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
};

export default TutorDetail;
